import * as types from "./types";
import { IModal } from "@/interfaces/IModal";
import { Commit } from "vuex";
import axios from "axios";
import {
	createInstanceAxios,
	deleteInstanceAxios,
	getEventLogAxios,
	getInstanceAxios,
	getInstancesAxios,
	getOptionListAxios,
	logoutInstanceAxios,
	qrCodeAxios,
	rebootInstanceAxios,
	sendEventLogAxios,
	setSettingsInstanceAxios,
} from "@/store/instance/services";

/**
 * Модалка. Уведомления (Колокольчик)
 * @param commit
 * @param payload
 */
export function modalNotifyAction(
	{ commit }: { commit: Commit },
	payload: IModal
): void {
	try {
		commit(types.MODAL_NOTIFY, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Модалка. Профиль
 * @param commit
 * @param payload
 */
export function modalProfileAction(
	{ commit }: { commit: Commit },
	payload: IModal
): void {
	try {
		commit(types.MODAL_PROFILE, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Модалка с фильтрами для мобильных устройств
 * @param commit
 * @param payload
 */
export function modalMobileFiltersAction(
	{ commit }: { commit: Commit },
	payload: IModal
): void {
	try {
		commit(types.MODAL_MOBILE_FILTERS, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Модалка - Активировать канал
 * @param commit
 * @param payload
 */
export function modalActivateChannelAction(
	{ commit }: { commit: Commit },
	payload: IModal
): void {
	try {
		commit(types.MODAL_ACTIVATE_CHANNEL, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Тип устройства (desktop or mobile)
 * @param commit
 * @param payload
 */
export function deviceTypeAction(
	{ commit }: { commit: Commit },
	payload: { isMobile: boolean }
): void {
	try {
		commit(types.DEVICE_TYPE, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Возвращает список инстансов
 * @param commit
 * @param payload
 */
export async function instancesAction(
	{ commit }: { commit: Commit },
	payload: {
		page?: number;
		partnerId?: number;
		instanceId?: string;
		token?: string;
		phone?: string;
		statusId?: string;
		channel?: string;
		expireOrder?: string;
		insertOrder?: string;
	}
): Promise<void> {
	try {
		const { data } = await getInstancesAxios(payload);
		commit(types.INSTANCES, data.data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Загружает карточку по ID и каналу, приводит строковый числовой ID к числу
 * и сохраняет данные вместе с состоянием запроса. Ошибки отражаются в хранилище:
 * только HTTP 403 считается отказом в доступе; результат вызова не возвращается.
 */
export async function instanceAction(
	{ commit }: { commit: Commit },
	payload: { instanceId: number; channel?: string }
): Promise<void> {
	try {
		commit(types.INSTANCE_LOAD_STATUS, "loading");
		// Сбрасываем прошлый инстанс, чтобы при ошибке доступа не показывать старую карточку.
		commit(types.INSTANCE, null);

		const { data } = await getInstanceAxios(payload);
		const instance = data?.data;
		let instanceId = instance?.instance;

		// API каналов может вернуть числовой ID строкой; компоненты ожидают число.
		// Пустую строку не преобразуем в 0, поскольку в ней нет идентификатора.
		if (typeof instanceId === "string" && instanceId.trim() !== "") {
			instanceId = Number(instanceId);
		}

		if (typeof instanceId !== "number" || !Number.isFinite(instanceId)) {
			// Пустой или некорректный ответ не подтверждает отсутствие прав пользователя.
			commit(types.INSTANCE_LOAD_STATUS, "error");
			return;
		}

		commit(types.INSTANCE, { ...instance, instance: instanceId });
		commit(types.INSTANCE_LOAD_STATUS, "loaded");
	} catch (e) {
		commit(types.INSTANCE, null);

		// Сетевые ошибки и ответы сервера без явного HTTP 403 остаются ошибками загрузки.
		const isAccessDenied = axios.isAxiosError(e) && e.response?.status === 403;
		const loadStatus = isAccessDenied ? "forbidden" : "error";
		commit(types.INSTANCE_LOAD_STATUS, loadStatus);

		console.log(e);
	}
}

/**
 * Создание инстанса
 * @param commit
 */
export async function createInstanceAction({
	commit,
}: {
	commit: Commit;
}): Promise<void> {
	try {
		const { data } = await createInstanceAxios();

		commit(types.INSTANCE_CREATE, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Переход на другую страницу
 * @param commit
 * @param payload
 */
export function redirectAction(
	{ commit }: { commit: Commit },
	payload: { path: string; page: number }
): void {
	try {
		commit(types.REDIRECT, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Удаление инстанса
 * @param commit
 * @param payload
 */
export async function deleteInstanceAction(
	{ commit }: { commit: Commit },
	payload: { instance: number; token?: string; channel?: string }
): Promise<void> {
	try {
		const { data } = await deleteInstanceAxios(payload);

		commit(types.INSTANCE_DELETE, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Модалка. Получить QR код
 * @param commit
 * @param payload
 */
export function modalQrCodeAction(
	{ commit }: { commit: Commit },
	payload: IModal
): void {
	try {
		commit(types.MODAL_QR_CODE, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Получить QR код
 * @param commit
 * @param payload
 */
export async function qrCodeAction(
	{ commit }: { commit: Commit },
	payload: {
		instance: number;
		token: string;
		channel?: string;
	}
): Promise<void> {
	try {
		const { data } = await qrCodeAxios(payload);
		commit(types.QR_CODE, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Перезагрузка инстанса
 * @param commit
 * @param payload
 */
export async function rebootInstanceAction(
	{ commit }: { commit: Commit },
	payload: {
		instance: number;
		token: string;
		channel?: string;
	}
): Promise<void> {
	try {
		const { data } = await rebootInstanceAxios(payload);

		commit(types.INSTANCE_REBOOT, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Logout instance
 * @param commit
 * @param payload
 */
export async function logoutInstanceAction(
	{ commit }: { commit: Commit },
	payload: {
		instance: number;
		token: string;
		channel?: string;
	}
): Promise<void> {
	try {
		const { data } = await logoutInstanceAxios(payload);

		commit(types.INSTANCE_LOGOUT, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Сохраняем настройки
 * @param commit
 * @param payload
 */
export async function setSettingsInstanceAction(
	{ commit }: { commit: Commit },
	payload: {
		instance: number;
		token: string;
		channel?: string;
		settings: object;
		field: string;
		oldValue: string;
		newValue: string;
	}
): Promise<void> {
	try {
		const { data } = await setSettingsInstanceAxios(payload);

		commit(types.INSTANCE_LOGOUT, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Модалка. Лог действий
 * @param commit
 * @param payload
 */
export function modalEventLogAction(
	{ commit }: { commit: Commit },
	payload: IModal
): void {
	try {
		commit(types.MODAL_EVENT_LOG, payload);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Лог действий
 * @param commit
 * @param payload
 */
export async function eventLogAction(
	{ commit }: { commit: Commit },
	payload: { instanceId: number }
): Promise<void> {
	try {
		const { data } = await getEventLogAxios(payload);
		commit(types.EVENT_LOG, data.data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Лог действий. Запись события
 * @param commit
 * @param payload
 */
export async function sendEventLogAction(
	{ commit }: { commit: Commit },
	payload: {
		eventLog: {
			instance: number;
			field: string;
			oldValue: string;
			newValue: string;
		};
	}
): Promise<void> {
	try {
		const { data } = await sendEventLogAxios(payload);

		commit(types.SEND_EVENT_LOG, data);
	} catch (e) {
		console.log(e);
	}
}

/**
 * Список опций для покупок
 * @param commit
 */
export async function optionListAction({
	commit,
}: {
	commit: Commit;
}): Promise<void> {
	try {
		const { data } = await getOptionListAxios();
		commit(types.OPTION_LIST, data);
	} catch (e) {
		console.log(e);
	}
}
